/* ============================================================
   APP LOADING INDICATOR
   ============================================================
   A circular buffering spinner in the Turbostart brand red, replacing
   the previous Lottie paper-plane animation.

   Built with CSS rather than a Lottie file so the colour comes from the
   shared --brand-red token (src/index.css) instead of being baked into
   an exported animation, and so no animation runtime is needed to draw
   it. Assets/loading.json is left in place, untouched, in case the
   previous animation is wanted again.

   Used in ~37 places via <KenLoader />; the component's public shape is
   unchanged, so no call site needed editing.
   ============================================================ */
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material';

const RING = 76; /* px — outer diameter of the spinner */
const BAR = 7; /* px — stroke thickness */

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    background: 'rgba(0,0,0,0.3)',
    zIndex: 99999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  /* The ring: a full border in a faint brand red with three of the four
     sides transparent, so what remains reads as an arc with a gap — the
     same shape as a standard buffering indicator. */
  spinner: {
    width: RING,
    height: RING,
    borderRadius: '50%',
    border: `${BAR}px solid var(--brand-red-soft, rgba(220,75,62,.18))`,
    borderTopColor: 'var(--brand-red, #DC4B3E)',
    borderRightColor: 'var(--brand-red, #DC4B3E)',
    boxSizing: 'border-box',
    animation: '$kenSpin 0.9s linear infinite',
    /* Respect users who have asked for reduced motion: the indicator
       still reads as "busy" without rotating quickly. */
    '@media (prefers-reduced-motion: reduce)': {
      animation: '$kenSpin 2.4s linear infinite',
    },
  },
  '@keyframes kenSpin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
}));

export default function KenLoader() {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      {/* role/aria so assistive tech announces the busy state — the
          Lottie player conveyed nothing to screen readers. */}
      <div
        className={classes.spinner}
        role="progressbar"
        aria-busy="true"
        aria-label="Loading"
      />
    </div>
  );
}
